import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaCheckCircle, FaCog, FaExclamationTriangle, FaPlug, FaSave } from "react-icons/fa";
import Spinner from "../../components/Spinner";
import ErrorPage from "../../components/ErrorPage";
import { LdsOverviewHero, LdsOverviewPage } from "../../components/lds/LdsOverview";
import http from "../../api/apiClient";
import { useCan } from "../../authz/useCan";
import { mapHttpErrorToMessage } from "../../utils/mapHttpErrorToMessage";
import { formatDutchDate } from "../../utils/dutchDate";
import ContributionLedgers from "./ContributionLedgers";
import ImportSchedule from "./ImportSchedule";
import ImportPeriod from "./ImportPeriod";
import PayerNameSetting from "./PayerNameSetting";
import EBoekhoudenConnection from "./EBoekhoudenConnection";
import EBoekhoudenStatusDot from "../../components/eboekhouden/EBoekhoudenStatusDot";

type Origin = "DATABASE" | "ENVIRONMENT" | "NONE";

export type EBoekhoudenSettings = {
  apiUrl?: string | null;
  apiUrlOrigin: Origin;
  source?: string | null;
  sourceOrigin: Origin;
  accessTokenConfigured: boolean;
  accessTokenOrigin: Origin;
  accessTokenHint?: string | null;
  encryptionAvailable: boolean;
  problem?: "ENCRYPTION_KEY_MISSING" | "TOKEN_UNREADABLE" | null;
  updatedAt?: string | null;
};

type TestResult = { ok: boolean; status: string; detail?: string | null; mutationCount?: number | null };

const ENDPOINT = "/finance/settings/eboekhouden";
const inputClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-950 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 disabled:bg-slate-50 disabled:text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:ring-indigo-950 dark:disabled:bg-slate-900/50 dark:disabled:text-slate-500";

export default function FinanceSettings(): ReactElement {
  const { t } = useTranslation();
  const canManage = useCan("finance.settings", "manage");
  const canImport = useCan("finance.contributions", "manage");

  const [settings, setSettings] = useState<EBoekhoudenSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ message?: string; status?: number } | null>(null);
  const [apiUrl, setApiUrl] = useState("");
  const [source, setSource] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const busyRef = useRef(false);

  const applySettings = (data: EBoekhoudenSettings) => {
    setSettings(data);
    // The fields only hold values stored in Lumen; empty means "use the environment".
    setApiUrl(data.apiUrlOrigin === "DATABASE" ? data.apiUrl || "" : "");
    setSource(data.sourceOrigin === "DATABASE" ? data.source || "" : "");
    setAccessToken("");
  };

  useEffect(() => {
    let cancelled = false;
    http.get(ENDPOINT)
      .then(({ data }) => {
        if (!cancelled) applySettings(data);
      })
      .catch((caught) => {
        if (!cancelled) setLoadError(mapHttpErrorToMessage(caught, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const errorText = (code?: string) => {
    switch (code) {
      case "API_URL_INVALID":
        return t("finance.settings.errors.api_url_invalid", {
          defaultValue: "Enter a valid https address (http only for localhost or host.docker.internal).",
        });
      case "SOURCE_INVALID":
        return t("finance.settings.errors.source_invalid", {
          defaultValue: "The source may contain at most 10 letters, digits, underscores or spaces.",
        });
      case "ENCRYPTION_KEY_MISSING":
        return t("finance.settings.errors.encryption_key_missing", {
          defaultValue: "No encryption key is configured on the server, so the token cannot be stored in Lumen. Set it in the environment instead.",
        });
      default:
        return t("finance.settings.errors.save_failed", { defaultValue: "Saving failed. Please try again." });
    }
  };

  const save = async (body: Record<string, unknown>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setSaving(true);
    setSaveMessage(null);
    setTestResult(null);
    try {
      const { data } = await http.put(ENDPOINT, body);
      applySettings(data);
      setSaveMessage({ ok: true, text: t("finance.settings.saved", { defaultValue: "Settings saved." }) });
    } catch (caught: any) {
      setSaveMessage({ ok: false, text: errorText(caught?.response?.data?.message) });
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save({ apiUrl: apiUrl.trim(), source: source.trim(), accessToken: accessToken.trim() || null });
  };

  const handleTest = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setTesting(true);
    setTestResult(null);
    setSaveMessage(null);
    try {
      const { data } = await http.post(`${ENDPOINT}/test`);
      setTestResult(data);
    } catch (caught: any) {
      setTestResult({ ok: false, status: "ERROR", detail: mapHttpErrorToMessage(caught, t)?.message });
    } finally {
      busyRef.current = false;
      setTesting(false);
    }
  };

  if (loading) return <Spinner />;
  if (loadError || !settings) {
    return <ErrorPage message={loadError?.message} status={loadError?.status} onRetry={() => window.location.reload()} />;
  }

  const originLabel = (origin: Origin) => {
    if (origin === "DATABASE") return t("finance.settings.origin.database", { defaultValue: "Stored in Lumen" });
    if (origin === "ENVIRONMENT") return t("finance.settings.origin.environment", { defaultValue: "From the environment" });
    return t("finance.settings.origin.none", { defaultValue: "Not set" });
  };
  const OriginBadge = ({ origin }: { origin: Origin }) => (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
        origin === "DATABASE"
          ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-200"
          : origin === "ENVIRONMENT"
            ? "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-slate-300"
            : "bg-amber-50 text-amber-700 dark:bg-amber-400/10 dark:text-amber-200"
      }`}
    >
      {originLabel(origin)}
    </span>
  );
  const fromEnvironment = (value?: string | null) =>
    value
      ? t("finance.settings.placeholder_env", { value, defaultValue: "From the environment: {{value}}" })
      : t("finance.settings.placeholder_none", { defaultValue: "Not set" });

  const tokenStatus = settings.accessTokenConfigured
    ? t("finance.settings.token_configured", {
      hint: settings.accessTokenHint ? `…${settings.accessTokenHint}` : "",
      defaultValue: "Configured {{hint}}",
    })
    : t("finance.settings.token_missing", { defaultValue: "No token configured" });

  const testText = (result: TestResult) => {
    switch (result.status) {
      case "OK":
        return t("finance.settings.test.ok", {
          count: result.mutationCount ?? 0,
          defaultValue: "Connection works. e-Boekhouden reports {{count}} mutations.",
        });
      case "DISABLED":
        return t("finance.settings.test.disabled", { defaultValue: "The connection is switched off. Switch it on first." });
      case "NOT_CONFIGURED":
        return t("finance.settings.test.not_configured", { defaultValue: "Not all settings are filled in (address, source and token)." });
      case "UNREACHABLE":
        return t("finance.settings.test.unreachable", { defaultValue: "e-Boekhouden cannot be reached at this address." });
      case "REJECTED":
        return t("finance.settings.test.rejected", { defaultValue: "e-Boekhouden refused the request. Check the token and the source." });
      default:
        return t("finance.settings.test.error", { defaultValue: "The test failed." });
    }
  };

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaCog}
        eyebrow={t("finance.overview.title", { defaultValue: "Finance" })}
        title={t("finance.settings.title", { defaultValue: "e-Boekhouden settings" })}
        description={t("finance.settings.description", {
          defaultValue: "Connection with the e-Boekhouden REST API. Values stored here win over the environment; leave a field empty to use the environment.",
        })}
        actions={<EBoekhoudenStatusDot />}
      />

      <EBoekhoudenConnection canManage={canManage} />

      <section className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6">
        {settings.problem && (
          <p role="alert" className="mb-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 dark:bg-amber-400/10 dark:text-amber-200">
            <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
            {settings.problem === "ENCRYPTION_KEY_MISSING"
              ? t("finance.settings.problem.key_missing", { defaultValue: "A token is stored, but the server has no encryption key to read it." })
              : t("finance.settings.problem.unreadable", { defaultValue: "The stored token cannot be read (was the encryption key changed?). Enter the token again." })}
          </p>
        )}

        <form onSubmit={handleSubmit} className="grid gap-5">
          <label className="grid gap-1.5">
            <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              {t("finance.settings.api_url", { defaultValue: "API address" })}
              <OriginBadge origin={settings.apiUrlOrigin} />
            </span>
            <input
              type="url"
              value={apiUrl}
              onChange={(event) => setApiUrl(event.target.value)}
              placeholder={settings.apiUrlOrigin === "DATABASE" ? "" : fromEnvironment(settings.apiUrl)}
              disabled={!canManage}
              className={inputClass}
            />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t("finance.settings.api_url_help", { defaultValue: "Usually https://api.e-boekhouden.nl/v1. For local testing: http://localhost:4010/v1 (mock)." })}
            </span>
          </label>

          <label className="grid gap-1.5">
            <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              {t("finance.settings.source", { defaultValue: "Source" })}
              <OriginBadge origin={settings.sourceOrigin} />
            </span>
            <input
              value={source}
              maxLength={10}
              onChange={(event) => setSource(event.target.value)}
              placeholder={settings.sourceOrigin === "DATABASE" ? "" : fromEnvironment(settings.source)}
              disabled={!canManage}
              className={`${inputClass} max-w-xs`}
            />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t("finance.settings.source_help", { defaultValue: "Name e-Boekhouden uses to recognise Lumen; max 10 characters." })}
            </span>
          </label>

          <label className="grid gap-1.5">
            <span className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-800 dark:text-slate-100">
              {t("finance.settings.access_token", { defaultValue: "API token" })}
              <OriginBadge origin={settings.accessTokenOrigin} />
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{tokenStatus}</span>
            </span>
            <input
              type="password"
              autoComplete="new-password"
              value={accessToken}
              onChange={(event) => setAccessToken(event.target.value)}
              placeholder={t("finance.settings.access_token_placeholder", { defaultValue: "Enter a new token to replace the current one" })}
              disabled={!canManage || !settings.encryptionAvailable}
              className={inputClass}
            />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {settings.encryptionAvailable
                ? t("finance.settings.access_token_help", {
                  defaultValue: "Create the token in e-Boekhouden (account settings). It is stored encrypted and never shown again.",
                })
                : t("finance.settings.errors.encryption_key_missing", {
                  defaultValue: "No encryption key is configured on the server, so the token cannot be stored in Lumen. Set it in the environment instead.",
                })}
            </span>
          </label>

          {canManage && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                disabled={saving || testing}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <FaSave aria-hidden="true" />
                {t("common.save", { defaultValue: "Save" })}
              </button>
              <button
                type="button"
                onClick={handleTest}
                disabled={saving || testing}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 hover:border-indigo-200 disabled:opacity-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
              >
                <FaPlug aria-hidden="true" />
                {testing
                  ? t("finance.settings.testing", { defaultValue: "Testing…" })
                  : t("finance.settings.test_button", { defaultValue: "Test connection" })}
              </button>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {t("finance.settings.test_hint", { defaultValue: "The test uses the saved settings." })}
              </span>
              {settings.accessTokenOrigin === "DATABASE" && (
                <button
                  type="button"
                  onClick={() => save({ clearAccessToken: true })}
                  disabled={saving || testing}
                  className="inline-flex h-10 items-center rounded-xl px-3 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-400/10"
                >
                  {t("finance.settings.clear_token", { defaultValue: "Remove stored token (use environment)" })}
                </button>
              )}
            </div>
          )}
        </form>

        {saveMessage && (
          <p role={saveMessage.ok ? "status" : "alert"} className={`mt-4 text-sm font-semibold ${saveMessage.ok ? "text-emerald-700 dark:text-emerald-300" : "text-red-600"}`}>
            {saveMessage.text}
          </p>
        )}
        {testResult && (
          <p
            role={testResult.ok ? "status" : "alert"}
            className={`mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-sm font-semibold ${
              testResult.ok
                ? "bg-emerald-50 text-emerald-800 dark:bg-emerald-400/10 dark:text-emerald-200"
                : "bg-red-50 text-red-700 dark:bg-red-400/10 dark:text-red-200"
            }`}
          >
            {testResult.ok ? <FaCheckCircle aria-hidden="true" className="mt-0.5 shrink-0" /> : <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />}
            <span>
              {testText(testResult)}
              {testResult.detail && <span className="block text-xs font-normal opacity-80">{testResult.detail}</span>}
            </span>
          </p>
        )}
        {settings.updatedAt && (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            {t("finance.settings.updated_at", { date: formatDutchDate(settings.updatedAt), defaultValue: "Last changed on {{date}}" })}
          </p>
        )}
      </section>

      <ContributionLedgers canManage={canManage} reloadKey={settings.updatedAt} />
      <ImportSchedule canManage={canManage} />
      <ImportPeriod canImport={canImport} />
      <PayerNameSetting canManage={canManage} />
    </LdsOverviewPage>
  );
}
